import { useState } from "react";
import { useKcToken } from "../../setup/auth/useKcToken";
import {
  EOrganizationState,
  useOrganizationState,
} from "../../setup/util/useOrganizationState";
import Spinner from "../common/Spinner";
import styles from "./styles.module.scss";
import CreateOrganizationForm from "./CreateOrganizationForm";
import OrganizationGateHeader from "./OrganizationGateHeader";
import OrganizationPending from "./OrganizationPending";
import OrganizationRejected from "./OrganizationRejected";

interface Props {
  children: React.ReactNode;
}

/**
 * Gates the portal dashboard behind the logged-in account's organization state:
 * no org yet -> registration form, PENDING -> under-review screen,
 * REJECTED -> rejection + resubmit, VERIFIED -> render children as-is.
 */
const OrganizationGate: React.FC<Props> = ({ children }) => {
  const token = useKcToken();
  const orgState = useOrganizationState(token);
  // Local override so the UI reflects a just-completed submission immediately,
  // without waiting on a token refresh or re-polling the fallback endpoint.
  const [justSubmitted, setJustSubmitted] = useState(false);

  if (justSubmitted) {
    return (
      <div className={styles.gateWrapper}>
        <OrganizationGateHeader />
        <OrganizationPending />
      </div>
    );
  }

  switch (orgState.state) {
    case EOrganizationState.LOADING:
      return (
        <div className={styles.container}>
          <Spinner />
        </div>
      );
    case EOrganizationState.NO_ORGANIZATION:
      return (
        <div className={styles.gateWrapper}>
          <OrganizationGateHeader />
          <div className={styles.container}>
            <CreateOrganizationForm
              onSubmitted={() => setJustSubmitted(true)}
            />
          </div>
        </div>
      );
    case EOrganizationState.PENDING:
      return (
        <div className={styles.gateWrapper}>
          <OrganizationGateHeader />
          <OrganizationPending />
        </div>
      );
    case EOrganizationState.REJECTED:
      return (
        <div className={styles.gateWrapper}>
          <OrganizationGateHeader />
          <OrganizationRejected
            rejectionReason={orgState.rejectionReason}
            code={orgState.code}
            name={orgState.name}
            logoUrl={orgState.logoUrl}
            websiteUrl={orgState.websiteUrl}
            description={orgState.description}
            onResubmit={() => setJustSubmitted(true)}
          />
        </div>
      );
    case EOrganizationState.VERIFIED:
    default:
      return <>{children}</>;
  }
};

export default OrganizationGate;
